export interface InheritingRepresentative<I> {
  readonly contextualRoleIds?: readonly I[] | null;
  readonly typeIds?: readonly I[] | null;
}

export interface InheritedMembership<I> {
  readonly contextualRoleIds?: readonly I[] | null;
  readonly representativeTypeIds?: readonly I[] | null;
}

export interface RepresentativeInheritance<I> {
  readonly contextualRoleIds: readonly I[];
  readonly typeIds: readonly I[];
  readonly areContextualRoleIdsFallback: boolean;
  readonly areTypeIdsFallback: boolean;
}

// A representative of an organization has its own contextual roles and representative types,
// else (absent) inherits the ones of its active membership of the organization, none without
// one. The caller gives the active membership only: an inactive one passes nothing on.
export const representativeInheritanceOf = <I>(
  representative: Readonly<InheritingRepresentative<I>>,
  activeMembership: Readonly<InheritedMembership<I>> | undefined,
): RepresentativeInheritance<I> => {
  const areContextualRoleIdsFallback =
    representative.contextualRoleIds === null ||
    representative.contextualRoleIds === undefined;
  const areTypeIdsFallback =
    representative.typeIds === null || representative.typeIds === undefined;

  return {
    contextualRoleIds:
      representative.contextualRoleIds ??
      activeMembership?.contextualRoleIds ??
      [],
    typeIds:
      representative.typeIds ?? activeMembership?.representativeTypeIds ?? [],
    areContextualRoleIdsFallback,
    areTypeIdsFallback,
  };
};

export interface StatusedMembership {
  readonly personId: string;
  readonly statusId?: string | null;
}

// The active membership of a person among the memberships of an organization (string ids).
export const activeMembershipOf = <M extends StatusedMembership>(
  memberships: readonly M[],
  personId: string,
  enabledStatusId: string,
): M | undefined =>
  memberships.find(
    (membership) =>
      membership.personId === personId &&
      membership.statusId === enabledStatusId,
  );
